import React from 'react';
import {Map} from 'immutable';
import {px, percent} from '@packages/systems/core/utils/WebflowCssSubset';
import {createRgbaColor} from '@packages/systems/core/utils/ColorUtils';
import {Atom} from '@packages/systems/wfdl';
import {TListOfElement} from '@packages/systems/wfdl-ast';
import {createIntegratedAtom} from '@packages/utilities/integrated-types';
import {NODE_TYPE_COMMERCE_CART_CONTAINER_WRAPPER} from '@packages/systems/commerce/constants';
import {EvaluatedNodeData, getChildren} from '@packages/utilities/data-utils';

const styleDisplayNone = Map({style: Map({display: 'none'})});

const CommerceCartContainerWrapper = Atom.createRender((props) => {
  const {attributes, classes, data, context, generateAttributes, renderChild} =
    props;
  const isOpen = context.get('isOpen');
  const styledAttributes = isOpen
    ? attributes
    : attributes.merge(styleDisplayNone);
  return (
    <div
      {...generateAttributes(styledAttributes, classes)}
      data-node-type={NODE_TYPE_COMMERCE_CART_CONTAINER_WRAPPER}
    >
      {getChildren(data).map((child: EvaluatedNodeData) => renderChild(child))}
    </div>
  );
});

/**
 * This is set to 1001 to be 1 value higher than the Navbar element (z-index: 1000).
 * The Navbar's z-index comes from Bootflow, making it trickier to coordinate.
 *
 * `public/css/lib/bootflow/bootflow-navbars.less`
 *
 * @see https://webflow.atlassian.net/browse/DYN-2416
 */
const CART_CONTAINER_Z_INDEX = 1001;

const config = createIntegratedAtom({
  displayName: 'Cart Wrapper',
  showEmpty: true,
  constraints: {
    wraps: ['CommerceCartContainer'],
    pinToParent: true,
  },
  style: {
    base: {
      main: {
        noPseudo: {
          position: 'fixed',
          left: px(0),
          top: px(0),
          right: px(0),
          bottom: px(0),
          zIndex: CART_CONTAINER_Z_INDEX,
          backgroundColor: createRgbaColor({r: 0, g: 0, b: 0, a: 0.8}),
        },
      },
    },
    conditional: {
      cartType: {
        modal: {
          main: {
            noPseudo: {
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
            },
          },
          tiny: {
            noPseudo: {
              flexDirection: 'row',
              justifyContent: 'center',
              alignItems: 'stretch',
            },
          },
        },
        leftSidebar: {
          main: {
            noPseudo: {
              // @TODO
              // This conditional style doesn't show in the Style Tab
              // because it relies on pluginContext and not node.data
              // which are not accounted for yet.
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'flex-start',
              alignItems: 'stretch',
            },
          },
        },
        rightSidebar: {
          main: {
            noPseudo: {
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'flex-end',
              alignItems: 'stretch',
            },
          },
        },
        leftDropdown: {
          main: {
            noPseudo: {
              position: 'absolute',
              top: percent(100),
              left: px(0),
              right: 'auto',
              bottom: 'auto',
              backgroundColor: createRgbaColor({r: 0, g: 0, b: 0, a: 0}),
              // maxHeight: // Calculated value that prevents dropdown from getting cut off.
            },
          },
          tiny: {
            noPseudo: {
              position: 'fixed',
              top: px(0),
              right: px(0),
              bottom: px(0),
              left: px(0),
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'center',
              alignItems: 'stretch',
            },
          },
        },
        rightDropdown: {
          main: {
            noPseudo: {
              position: 'absolute',
              left: 'auto',
              top: percent(100),
              right: px(0),
              bottom: 'auto',
              backgroundColor: createRgbaColor({r: 0, g: 0, b: 0, a: 0}),
              // maxHeight: // Calculated value that prevents dropdown from getting cut off.
            },
          },
          tiny: {
            noPseudo: {
              position: 'fixed',
              top: px(0),
              right: px(0),
              bottom: px(0),
              left: px(0),
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'center',
              alignItems: 'stretch',
            },
          },
        },
      },
    },
  },
  lockedStyles: {
    position: {
      when: true,
      message:
        'The cart container wrapper `position` property can’t be changed.',
    },
    display: {
      when: true,
      message:
        'The cart container wrapper `display` property can’t be changed.',
    },
    flexDirection: {
      when: true,
      message:
        'The cart container wrapper `flex-direction` property can’t be changed.',
    },
    justifyContent: {
      when: true,
      message:
        'The cart container wrapper `justify-content` property can’t be changed.',
    },
    alignItems: {
      when: true,
      message:
        'The cart container wrapper `align-items` property can’t be changed.',
    },
    left: {
      when: true,
      message: 'The cart container wrapper `left` property can’t be changed.',
    },
    top: {
      when: true,
      message: 'The cart container wrapper `top` property can’t be changed.',
    },
    right: {
      when: true,
      message: 'The cart container wrapper `right` property can’t be changed.',
    },
    bottom: {
      when: true,
      message: 'The cart container wrapper `bottom` property can’t be changed.',
    },
    marginTop: {
      when: true,
      message:
        'The cart container wrapper `margin-top` property can’t be changed.',
    },
    marginBottom: {
      when: true,
      message:
        'The cart container wrapper `margin-bottom` property can’t be changed.',
    },
    marginLeft: {
      when: true,
      message:
        'The cart container wrapper `margin-left` property can’t be changed.',
    },
    marginRight: {
      when: true,
      message:
        'The cart container wrapper `margin-right` property can’t be changed.',
    },
    paddingTop: {
      when: true,
      message:
        'The cart container wrapper `padding-top` property can’t be changed.',
    },
    paddingBottom: {
      when: true,
      message:
        'The cart container wrapper `padding-bottom` property can’t be changed.',
    },
    paddingLeft: {
      when: true,
      message:
        'The cart container wrapper `padding-left` property can’t be changed.',
    },
    paddingRight: {
      when: true,
      message:
        'The cart container wrapper `padding-right` property can’t be changed.',
    },
    width: {
      when: true,
      message: 'The cart container wrapper `width` property can’t be changed.',
    },
    height: {
      when: true,
      message: 'The cart container wrapper `height` property can’t be changed.',
    },
    maxWidth: {
      when: true,
      message:
        'The cart container wrapper `max-width` property can’t be changed.',
    },
    maxHeight: {
      when: true,
      message:
        'The cart container wrapper `max-height` property can’t be changed.',
    },
    minWidth: {
      when: true,
      message:
        'The cart container wrapper `min-width` property can’t be changed.',
    },
    minHeight: {
      when: true,
      message:
        'The cart container wrapper `min-height` property can’t be changed.',
    },
    float: {
      when: true,
      message: 'The cart container wrapper `float` property can’t be changed.',
    },
    clear: {
      when: true,
      message: 'The cart container wrapper `clear` property can’t be changed.',
    },
    overflow: {
      when: true,
      message:
        'The cart container wrapper `overflow` property can’t be changed.',
    },
  },
  render: CommerceCartContainerWrapper,
  fields: {
    children: TListOfElement,
  },
});

export default config;
